import { formatYmdAsCrmDate } from '../utils/dateTime';
import { getDbSupabase } from './supabase';

export type AccountCustomerComment = {
  commentDate: string;
  customerNo: string;
  customerName: string;
  commentText: string;
  userIds: string;
};

export type AccountCustomerCommentsResult = {
  comments: AccountCustomerComment[];
  motherCode: string;
};

function formatCommentDate(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return '';
  // Prefer YYYY-MM-DD from date/timestamptz strings.
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : text;
}

export async function fetchAccountCustomerComments(
  motherCode: string,
): Promise<AccountCustomerCommentsResult> {
  const code = motherCode.trim();
  if (!code) {
    throw new Error('Account code is required.');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy will reject unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?mother_code=${encodeURIComponent(code)}&resource=comments`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error(
      (json?.error || `Failed to load comments (${response.status}).`) + detail + hint,
    );
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid comments response.');
  }

  const raw = Array.isArray(json.comments) ? json.comments : [];
  const comments: AccountCustomerComment[] = raw
    .map((row) => {
      const item = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
      return {
        commentDate: formatCommentDate(item.comment_date),
        customerNo: String(item.customer_no ?? '').trim(),
        customerName: String(item.customer_name ?? '').trim(),
        commentText: String(item.comment_text ?? '').trim(),
        userIds: String(item.user_ids ?? '').trim(),
      };
    })
    .filter((row) => row.commentText.length > 0);

  const meta =
    json.meta && typeof json.meta === 'object'
      ? (json.meta as Record<string, unknown>)
      : {};

  return {
    comments,
    motherCode: String(meta.mother_code ?? code).trim() || code,
  };
}

export function formatAccountCommentDate(isoDate: string): string {
  const text = isoDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return text || '—';
  return formatYmdAsCrmDate(text);
}
